import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import GQL from '@lib/cms/gql';
import FlexibleDataOptimized from '@lib/cms/flexibleDataOptimized';
import { ALL_BUILDER_DEVELOPMENT_QUERY } from '@lib/fragments';

import Container from '@layout/container';
import Grid from '@layout/grid';
import FlexiblePage from '@templates/FlexiblePage';
import Select from '@components/select';
import ArticleCard from '@components/article-card';

import { search_bar, state_sort, search, card_outer, zero_state } from './styles.module.scss';

export async function getStaticProps(context) {
  const preview = context.preview || false;
  const previewEnv = context?.previewData?.environment ?? null;
  const landingData = await FlexibleDataOptimized.fetch('builder', preview, previewEnv);

  const builderData = await GQL.fetch(
    ALL_BUILDER_DEVELOPMENT_QUERY,
    { category: 'Builder' },
    { preview },
    preview,
    previewEnv
  );

  const allBuilders = builderData?.data?.pageBuilderdevelopmentCollection?.items ?? [];

  // Create service area object with respective counts of Builders
  const serviceAreas = {};
  allBuilders.forEach((builder) => {
    serviceAreas[builder.serviceArea] = (serviceAreas[builder.serviceArea] || 0) + 1;
  });
  // Coerce to array, sort by count, create label/value object
  const serviceAreaOptions = Object.entries(serviceAreas)
    .sort(([, a], [, b]) => b - a)
    .map(([el]) => {
      return { label: el, value: el };
    });

  return {
    props: { allBuilders, landingData, preview, serviceAreaOptions }
  };
}

const BuilderLandingPage = ({ allBuilders, landingData, serviceAreaOptions, preview }) => {
  const router = useRouter();
  const topRef = useRef();
  const [areaValue, setAreaValue] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter Builders by service Area & 'starts with' input text
  const filteredBuilders = allBuilders.filter((builder) => {
    const serviceAreaMatch = areaValue === 'all' || areaValue === builder.serviceArea;

    // Searching by builderDevelopmentName, Nearby Community titles, and associated development names
    const searchTermMatch =
      builder?.builderDevelopmentName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      builder?.nearbyCommunities?.items?.filter((com) =>
        com?.title?.toLowerCase().includes(searchTerm?.toLowerCase())
      ).length > 0 ||
      builder?.nearbyCommunities?.items?.filter((dev) =>
        dev?.builderDevelopmentName?.toLowerCase().includes(searchTerm?.toLowerCase())
      ).length > 0;

    return serviceAreaMatch && searchTermMatch;
  });

  useEffect(() => {
    if (router?.isReady) {
      const area = router?.query?.area;
      setAreaValue(area || 'all');
    }

    return () => {
      setAreaValue();
    };
  }, [router]);

  const handleSelectChange = (newAreaValue) => {
    setAreaValue(newAreaValue);
    router.push({ pathname: router.pathname, query: { area: newAreaValue } }, undefined, {
      shallow: true
    });
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    router.push({ pathname: router.pathname, query: { ...router.query } }, undefined, {
      shallow: true
    });
  };

  return (
    <FlexiblePage data={landingData} preview={preview}>
      {landingData && (
        <main>
          <Container spacing="large" gridOptions={{ layout: 'single' }}>
            <div ref={topRef} className={search_bar}>
              <div className={state_sort}>
                <Select
                  options={[{ label: 'All', value: 'all' }, ...serviceAreaOptions]}
                  onChange={handleSelectChange}
                  selected={areaValue}
                />
              </div>
              <div className={search}>
                <input
                  tabIndex="0"
                  value={searchTerm}
                  onChange={handleInputChange}
                  placeholder="Search by Builder Name"
                />
              </div>
            </div>

            {filteredBuilders.length > 0 ? (
              <Grid gutter="20" layout="3">
                {filteredBuilders.map((builder) => {
                  return (
                    <div className={card_outer} key={builder?.slug}>
                      <ArticleCard
                        description={builder?.excerpt}
                        eyebrow={builder.serviceArea.toUpperCase()}
                        image={
                          builder?.featuredImage || {
                            height: 960,
                            url: 'https://images.ctfassets.net/wzzep3ntdgx4/4V3cWpoV2LctBsJYc6SnSf/4ef45ecd14a78222b508078d9fe9d345/AdobeStock_376819599.jpeg',
                            width: 1440
                          }
                        }
                        link={{
                          aria: 'Explore this builder',
                          icon: true,
                          label: 'Explore',
                          url: `/builder/${builder?.slug}`
                        }}
                        theme="community"
                        title={builder?.builderDevelopmentName}
                      />
                    </div>
                  );
                })}
              </Grid>
            ) : (
              <div className={zero_state}>
                {areaValue !== 'all'
                  ? 'No Builders available under this filter. Try selecting another.'
                  : 'No Builders meet your search criteria .'}
              </div>
            )}
          </Container>
        </main>
      )}
    </FlexiblePage>
  );
};

export default BuilderLandingPage;
